import { Button, buttonClass, fieldClass, selectClass } from '@ledgerloom/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { propose } from '../api';
import { useApi, usePageTitle, useWorkspaceCurrency } from '../hooks';
import { useSession } from '../session';
import { Empty, ErrorBanner, Icon, Loading, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { CardBoard, changedCards, type CardField, type KanbanCard } from '../components/CardBoard';
import { money, toMinor } from '../format';

// The pipeline board: open deals in columns by stage, on the card board
// (data-display/kanban-board). A move is a deal.move_stage proposal, a new
// owner or title a deal.update, a quick add a deal.create; the board shows the
// gate's answer once the pipeline reloads.

interface BoardDeal { id: string; title: string; stage_id: string; value_minor: string; currency: string; probability: number; expected_close: string | null; owner_user_id: string | null; owner_name: string | null; party_name: string | null; organization_name: string | null; rotting: boolean; version: number }
interface Board { pipeline: { id: string; name: string }; stages: Array<{ id: string; key: string; name: string; probability: number; status: 'active' | 'archived'; deals: BoardDeal[]; count: number; value_minor: string; weighted_minor: string }> }
interface Pipelines { pipelines: Array<{ id: string; name: string; is_default: boolean }> }
// The stage gate's names for a deal's fields (a stage's required_fields), as people read them.
const FIELD_LABEL: Record<string, string> = { value: 'Value', expected_close: 'Expected close', organization_id: 'Organization' };
const fieldLabel = (k: string) => FIELD_LABEL[k] ?? k.replace(/_/g, ' ');
// What a person does to supply each field on the card; a field the card cannot set says so.
const HOW: Record<string, string> = { value: "type the deal's value in Value below and press Enter", expected_close: 'pick the expected close date below' };
const how = (k: string) => HOW[k] ?? `the deal needs ${fieldLabel(k)}, which the board cannot set yet`;
// A move the stage refused for missing fields: the stage it was going to, every field it asked for
// (kept across retries, so steps count from the first refusal), and the one step to show now.
interface Blocked { stage: string; steps: string[]; message: string; fields: string[] }
// The step the person is on: the first field the stage still lacks, as one instruction.
function nextStep(title: string, stageName: string, steps: string[], missing: string[]) {
  const at = steps.find((k) => missing.includes(k)) ?? missing[0]!;
  const n = steps.indexOf(at) + 1;
  const last = steps.every((k) => k === at || !missing.includes(k));
  return {
    field: at,
    message: `${steps.length > 1 ? `Step ${n} of ${steps.length} to move ${title} to ${stageName}: ` : `To move ${title} to ${stageName}, `}${how(at)}.${HOW[at] && last ? ` ${title} moves there once it is saved.` : ''}`,
  };
}

export function Pipeline() {
  const pipelines = useApi<Pipelines>('/v1/pipelines');
  const [chosen, setChosen] = useState<string | null>(null);
  const pid = chosen ?? pipelines.data?.pipelines.find((p) => p.is_default)?.id ?? pipelines.data?.pipelines[0]?.id ?? null;
  const board = useApi<Board>(pid ? `/v1/pipelines/${pid}/board` : null, [pid]);
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  const { me, refreshStats } = useSession();
  usePageTitle('Pipeline');

  const users = useApi<Array<{ id: string; display_name: string; status: string }>>('/v1/users');
  const currency = useWorkspaceCurrency();
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  // What the board shows until the gate answers and the pipeline reloads.
  const [local, setLocal] = useState<KanbanCard[] | null>(null);
  const [blocked, setBlocked] = useState<ReadonlyMap<string, Blocked>>(new Map());
  const deals = useMemo(() => new Map(board.data?.stages.flatMap((s) => s.deals).map((d) => [d.id, d]) ?? []), [board.data]);
  const fromServer = useMemo(() => board.data?.stages.flatMap((s) => s.deals.map((d): KanbanCard => ({
    id: d.id, title: d.title, status: d.stage_id, assignee: d.owner_user_id, subtitle: d.organization_name ?? d.party_name ?? 'No contact',
    meta: money(d.value_minor, d.currency, { cents: false }), flags: d.rotting ? ['Stalled'] : [], href: `/deals/${d.id}`,
    fields: [
      { key: 'value', label: `Value (${d.currency})`, type: 'number', value: Number(d.value_minor) ? String(Number(d.value_minor) / 10_000) : '' },
      { key: 'expected_close', label: 'Expected close', type: 'date', value: d.expected_close?.slice(0, 10) ?? '' },
    ] satisfies CardField[],
  }))) ?? [], [board.data]);
  const cards = (local ?? fromServer).map((c) => {
    const b = blocked.get(c.id);
    // The same object when nothing is added, so the board's memoized cards skip the render.
    return !b && !busy.has(c.id) ? c : { ...c, ...(busy.has(c.id) ? { busy: true } : {}), ...(b ? { problem: { message: b.message, fields: b.fields } } : {}) };
  });
  // Everyone who can own a deal, and every owner already on one.
  const people = useMemo(() => {
    const list = (users.data ?? []).filter((u) => u.status === 'active').map((u) => ({ id: u.id, name: u.display_name }));
    for (const d of deals.values()) if (d.owner_user_id && !list.some((p) => p.id === d.owner_user_id)) list.push({ id: d.owner_user_id, name: d.owner_name ?? 'Someone' });
    if (!list.some((p) => p.id === me.user_id)) list.unshift({ id: me.user_id, name: me.display_name });
    return list;
  }, [users.data, deals, me]);

  async function apply(next: KanbanCard[]) {
    const changes = changedCards(local ?? fromServer, next);
    setLocal(next);
    const ids = changes.map((c) => c.card.id);
    setBusy((b) => new Set([...b, ...ids]));
    const stage = (id: string) => board.data?.stages.find((s) => s.id === id)?.name ?? 'the new stage';
    const block = (id: string, b: Blocked | null) => setBlocked((m) => { const n = new Map(m); if (b) n.set(id, b); else n.delete(id); return n; });
    // A move a stage refuses for missing fields opens the card on them; once they are saved, the move is tried again.
    const move = async (d: BoardDeal, to: string, version: number) => {
      const out = await propose('deal.move_stage', { deal_id: d.id, base_version: version, stage_id: to });
      const need = out.reasons?.find((r) => r.rule_id === 'deal.stage_required_fields')?.details as { missing?: string[] } | undefined;
      if (out.state === 'rejected' && need?.missing?.length) {
        const prior = blocked.get(d.id);
        const steps = prior?.stage === to ? prior.steps : need.missing;
        // shortcut: the card edits only value and expected close; a stage needing another field (organization) says so, until the board gets a picker for it.
        const step = nextStep(d.title, stage(to), steps, need.missing);
        block(d.id, { stage: to, steps, fields: [step.field], message: step.message });
        return version;
      }
      block(d.id, null);
      toast(outcomeMessage(out, `Moved ${d.title} to ${stage(to)}`), outcomeTone(out));
      return out.state === 'written' ? version + 1 : version;
    };
    try {
      for (const { card, was } of changes) {
        const d = deals.get(card.id);
        if (!was || !d) {
          if (!currency) throw new Error('The workspace currency is still loading. Try again.');
          const out = await propose('deal.create', { title: card.title, value: { minor: toMinor('0'), scale: 4, currency }, pipeline_id: board.data!.pipeline.id, stage_id: card.status, ...(card.assignee ? { owner_user_id: card.assignee } : {}) });
          toast(outcomeMessage(out, `Created ${card.title}`), outcomeTone(out));
          continue;
        }
        let version = d.version;
        if (card.status !== was.status) version = await move(d, card.status, version);
        const field = (k: string) => card.fields?.find((f) => f.key === k)?.value ?? '';
        const prior = (k: string) => was.fields?.find((f) => f.key === k)?.value ?? '';
        const set = {
          ...(card.title !== was.title ? { title: card.title } : {}),
          ...(card.assignee !== was.assignee ? { owner_user_id: card.assignee } : {}),
          ...(field('value') !== prior('value') ? { value: { minor: toMinor(field('value') || '0'), scale: 4, currency: d.currency } } : {}),
          ...(field('expected_close') !== prior('expected_close') ? { expected_close: field('expected_close') || null } : {}),
        };
        if (Object.keys(set).length) {
          const out = await propose('deal.update', { deal_id: d.id, base_version: version, set });
          toast(outcomeMessage(out, `Saved ${card.title}`), outcomeTone(out));
          const waiting = blocked.get(d.id);
          if (out.state === 'written' && waiting && card.status === was.status) await move(d, waiting.stage, version + 1);
        }
      }
    } catch (e) { toast((e as Error).message, 'error'); }
    refreshStats();
    await board.reload();
    // shortcut: a second change sent while the first is in flight shows the server's board when the first lands; queue them if that bites.
    setLocal(null); setBusy((b) => new Set([...b].filter((id) => !ids.includes(id))));
  }

  // Totals are sums of minor units, so they mean something only when every deal shares one currency.
  const currencies = [...new Set(board.data?.stages.flatMap((s) => s.deals.map((d) => d.currency)) ?? [])];
  const one = currencies.length === 1 ? currencies[0]! : null;
  // Minor-unit totals are bigint strings; add them exactly.
  const sum = (pick: (s: Board['stages'][number]) => string) => String(board.data?.stages.reduce((n, s) => n + BigInt(pick(s)), BigInt(0)) ?? 0);
  const total = sum((s) => s.value_minor);
  const weighted = sum((s) => s.weighted_minor);
  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Pipeline</h1><p>{board.data && one ? `${money(total, one, { cents: false })} open, ${money(weighted, one, { cents: false })} weighted by probability.` : board.data && currencies.length > 1 ? `Open deals in ${currencies.join(', ')}; totals are shown per deal.` : 'Open deals by stage.'}</p></div>
        <div className="row">
          {(pipelines.data?.pipelines.length ?? 0) > 1 && (
            <><label htmlFor="pipeline-pick" className="sr-only">Pipeline</label>
              <select id="pipeline-pick" className={selectClass} style={{ width: 'auto' }} value={pid ?? ''} onChange={(e) => setChosen(e.target.value)}>{pipelines.data!.pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></>
          )}
          <Link className={buttonClass({ variant: 'secondary' })} to="/deals">All deals</Link>
          <Button type="submit" onClick={() => setCreating(true)}><Icon name="plus" />New deal</Button>
        </div>
      </div>
      <ErrorBanner error={pipelines.error ?? board.error} />
      {!board.data ? (board.loading || pipelines.loading ? <Loading label="Loading the pipeline" /> : <Empty title="No pipeline yet" />) : (
        <CardBoard title={board.data.pipeline.name} names={{ status: 'Stage', assignee: 'Owner' }} fixed={['description']}
          columns={board.data.stages.map((s) => ({ id: s.id, title: s.name, closed: s.status === 'archived', note: [s.status === 'archived' ? 'Archived' : '', one ? money(s.value_minor, one, { cents: false }) : '', `${s.probability}%`].filter(Boolean).join(' · ') }))}
          people={people} currentUser={me.user_id} cards={cards} onCardsChange={(next) => void apply(next)} />
      )}
      {creating && board.data && <NewDeal stages={board.data.stages} pipelineId={board.data.pipeline.id} onClose={() => setCreating(false)} onDone={() => { setCreating(false); void board.reload(); }} />}
    </div>
  );
}

function NewDeal({ stages, pipelineId, onClose, onDone }: { stages: Board['stages']; pipelineId: string; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ title: '', value: '', stage: stages[0]?.id ?? '', close: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const currency = useWorkspaceCurrency();
  async function save() {
    if (!currency) { setError(new Error('The workspace currency is still loading. Try again.')); return; }
    setBusy(true); setError(null);
    try {
      const out = await propose('deal.create', { title: form.title, value: { minor: toMinor(form.value || '0'), scale: 4, currency }, pipeline_id: pipelineId, stage_id: form.stage, ...(form.close ? { expected_close: form.close } : {}) });
      toast(outcomeMessage(out, 'Deal created'), outcomeTone(out));
      if (out.state !== 'rejected') onDone(); else setError(new Error(outcomeMessage(out)));
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <ConfirmDialog open title="New deal" confirmLabel="Create deal" busy={busy} onConfirm={save} onCancel={onClose}>
      <ErrorBanner error={error} />
      <Field label="Title" required><input className={fieldClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
      <div className="form-grid">
        <Field label={`Value (${currency ?? '…'})`} required><input className={fieldClass} inputMode="decimal" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Field>
        <Field label="Stage"><select className={selectClass} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
        <Field label="Expected close"><input className={fieldClass} type="date" value={form.close} onChange={(e) => setForm({ ...form, close: e.target.value })} /></Field>
      </div>
    </ConfirmDialog>
  );
}
